"use client";

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import type { SearchEntityResult } from "fansunited-data-layer";
import type { LivescoreSport, LivescoreFilter } from "@/views/livescore/utils/livescore";
import { todayISODate } from "@/utilities/formatting/date";

type EntitiesMap = Record<string, SearchEntityResult>;

interface LivescoreState {
    sport: LivescoreSport;
    setSport: (s: LivescoreSport) => void;
    filter: LivescoreFilter;
    setFilter: (f: LivescoreFilter) => void;
    date: string;
    setDate: (d: string) => void;
    activeCompetitionId: string | null;
    setActiveCompetitionId: (id: string | null) => void;
    entities: EntitiesMap;
    setEntities: (e: EntitiesMap) => void;
}

const Ctx = createContext<LivescoreState | null>(null);

interface ProviderProps {
    children: React.ReactNode;
    initialSport?: LivescoreSport;
    initialFilter?: LivescoreFilter;
    initialDate?: string;
    initialActiveCompetitionId?: string | null;
    /**
     * Seed the entities map with server-hydrated metadata (e.g., popular
     * competitions' names/logos). Subsequent client-side livescore fetches
     * merge into this map — server-hydrated entries persist until overwritten.
     */
    initialEntities?: EntitiesMap;
}

export function LivescoreStateProvider({
    children,
    initialSport = "football",
    initialFilter = "all",
    initialDate = todayISODate(),
    initialActiveCompetitionId = null,
    initialEntities,
}: ProviderProps) {
    const [sport, setSport] = useState<LivescoreSport>(initialSport);
    const [filter, setFilter] = useState<LivescoreFilter>(initialFilter);
    const [date, setDate] = useState<string>(initialDate);
    const [activeCompetitionId, setActiveCompetitionId] = useState<string | null>(initialActiveCompetitionId);
    const [entities, setEntitiesState] = useState<EntitiesMap>(initialEntities ?? {});

    // When the server re-renders this provider with a new `initialEntities` map
    // (e.g., after a locale change the parent streams a fresh set of
    // locale-correct entities), re-seed state so the sidebar reflects the new
    // language. Compare by referential identity — the server builds a new map
    // on each render — to avoid stomping client-side merges within a locale.
    const lastInitialEntitiesRef = useRef(initialEntities);
    useEffect(() => {
        if (initialEntities && initialEntities !== lastInitialEntitiesRef.current) {
            lastInitialEntitiesRef.current = initialEntities;
            setEntitiesState(initialEntities);
        }
    }, [initialEntities]);

    // Merge writes so server-hydrated entries survive later client-side fetches
    // that omit them (e.g., popular competitions with no matches today).
    const setEntities = React.useCallback((next: EntitiesMap) => {
        setEntitiesState((prev) => ({ ...prev, ...next }));
    }, []);

    return (
        <Ctx.Provider
            value={{
                sport,
                setSport,
                filter,
                setFilter,
                date,
                setDate,
                activeCompetitionId,
                setActiveCompetitionId,
                entities,
                setEntities,
            }}
        >
            {children}
        </Ctx.Provider>
    );
}

/**
 * Read livescore state from context; if no provider wraps the caller, manages
 * its own internal state so LivescoreClient and LivescoreBlock keep working
 * standalone.
 */
export function useLivescoreState(defaults?: {
    sport?: LivescoreSport;
    filter?: LivescoreFilter;
}): LivescoreState {
    const ctx = useContext(Ctx);
    const [sport, setSport] = useState<LivescoreSport>(defaults?.sport ?? "football");
    const [filter, setFilter] = useState<LivescoreFilter>(defaults?.filter ?? "all");
    const [date, setDate] = useState<string>(todayISODate());
    const [activeCompetitionId, setActiveCompetitionId] = useState<string | null>(null);
    const [entities, setEntitiesState] = useState<EntitiesMap>({});
    // Merge like the provider does, so per-group hydration survives the next poll.
    const setEntities = React.useCallback((next: EntitiesMap) => {
        setEntitiesState((prev) => ({ ...prev, ...next }));
    }, []);

    if (ctx) return ctx;
    return {
        sport,
        setSport,
        filter,
        setFilter,
        date,
        setDate,
        activeCompetitionId,
        setActiveCompetitionId,
        entities,
        setEntities,
    };
}
